import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import './FlordvorDashboard.css';

const TABS = [
  ['summary', 'Сводка'], ['wholesale', 'Группы опта'], ['retail', 'Розница'],
  ['operations', 'Списания и приёмки'], ['preorders', 'Предзаказы'],
  ['dynamics', 'Динамика поставок'], ['method', 'Методика'],
];
const IMPORT_KINDS = [
  ['verified_snapshot', 'Проверенный снимок'], ['history', 'История'],
  ['incremental_cache', 'Кеш догрузки'], ['preorder_snapshots', 'Снимки предзаказов'],
];

export function formatNumber(value, digits = 0) {
  if (value === null || value === undefined || value === '') return '—';
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: digits }).format(value);
}
export const formatMoney = value => value === null || value === undefined || value === '' ? '—' : `${formatNumber(value, 2)} ₽`;
export function formatSourceTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' });
}

function Metric({ label, value, previous, money = false }) {
  const show = money ? formatMoney : value => formatNumber(value, 2);
  return <div className="flordvor-metric"><span>{label}</span><strong>{show(value)}</strong>{previous !== undefined && <small>Ранее: {show(previous)}</small>}</div>;
}

function DataTable({ columns, rows, empty = 'Нет данных' }) {
  if (!rows?.length) return <p className="flordvor-empty">{empty}</p>;
  return <div className="flordvor-table-wrap"><table className="flordvor-table">
    <thead><tr>{columns.map(column => <th key={column.key}>{column.label}</th>)}</tr></thead>
    <tbody>{rows.map((row, index) => <tr key={row.id || row.key || row.name || index}>
      {columns.map(column => <td key={column.key}>{column.render ? column.render(row) : (row[column.key] ?? '—')}</td>)}
    </tr>)}</tbody>
  </table></div>;
}

const financialColumns = [
  { key: 'name', label: 'Группа' },
  { key: 'clients', label: 'Клиенты', render: row => formatNumber(row.clients) },
  { key: 'orders', label: 'Заказы', render: row => formatNumber(row.orders) },
  { key: 'quantity', label: 'Количество', render: row => formatNumber(row.quantity) },
  { key: 'grossRevenue', label: 'До возвратов', render: row => formatMoney(row.grossRevenue) },
  { key: 'returns', label: 'Возвраты', render: row => formatMoney(row.returns) },
  { key: 'revenue', label: 'Выручка', render: row => formatMoney(row.revenue) },
  { key: 'profit', label: 'Прибыль', render: row => formatMoney(row.profit) },
];

function Summary({ period }) {
  if (!period) return <p className="flordvor-empty">В снимке нет периодов.</p>;
  return <><div className="flordvor-metrics">
    <Metric label="Выручка после возвратов" value={period.revenue} previous={period.previousRevenue} money />
    <Metric label="Валовая прибыль" value={period.profit} previous={period.previousProfit} money />
    <Metric label="Клиенты" value={period.clients} previous={period.previousClients} />
    <Metric label="Продажи" value={period.sales} previous={period.previousSales} />
    <Metric label="Заказы" value={period.orders} previous={period.previousOrders} />
    <Metric label="Количество товара" value={period.quantity} previous={period.previousQuantity} />
  </div>{period.historyLimited && <div className="flordvor-warning">История этого периода ограничена данными источника.</div>}
  <p className="flordvor-note">Период: {period.from || '—'} — {period.to || '—'}. Возвраты: {formatMoney(period.returns)}.</p></>;
}

function Retail({ retail }) {
  if (!retail) return <p className="flordvor-empty">Розничная детализация отсутствует.</p>;
  return <><div className="flordvor-metrics">
    <Metric label="Выручка розницы" value={retail.revenue} previous={retail.previousRevenue} money />
    <Metric label="Прибыль розницы" value={retail.profit} previous={retail.previousProfit} money />
    <Metric label="Клиенты" value={retail.clients} previous={retail.previousClients} />
    <Metric label="Продажи" value={retail.sales} previous={retail.previousSales} />
  </div><DataTable columns={financialColumns} rows={retail.channels || []} /></>;
}

function Operations({ period }) {
  const supplierColumns = [
    { key: 'name', label: 'Поставщик' }, { key: 'amount', label: 'Сумма приёмок', render: row => formatMoney(row.amount) },
    { key: 'count', label: 'Документы', render: row => formatNumber(row.count) },
    { key: 'assortmentCount', label: 'Уникальные позиции', render: row => formatNumber(row.assortmentCount) },
    { key: 'quantity', label: 'Количество', render: row => formatNumber(row.quantity) },
  ];
  return <><h3>Приёмки</h3><DataTable columns={supplierColumns} rows={period?.suppliers || []} />
    <h3>Списания и возвраты</h3><div className="flordvor-metrics">
      <Metric label="Списания" value={period?.loss?.gross} previous={period?.loss?.previousGross} money />
      <Metric label="Оприходования" value={period?.loss?.enter} previous={period?.loss?.previousEnter} money />
      <Metric label="Возвраты" value={period?.loss?.returns} previous={period?.loss?.previousReturns} money />
    </div><DataTable columns={[
      { key: 'name', label: 'Статус' }, { key: 'amount', label: 'Сумма', render: row => formatMoney(row.amount) },
      { key: 'previousAmount', label: 'Ранее', render: row => formatMoney(row.previousAmount) },
    ]} rows={period?.loss?.states || []} /></>;
}

function Preorders({ period }) {
  return <DataTable columns={[
    { key: 'date', label: 'Дата поставки' }, { key: 'orders', label: 'Текущие заказы', render: row => formatNumber(row.orders) },
    { key: 'snapshotOrders', label: 'Предзаказ в снимке', render: row => formatNumber(row.snapshotOrders) },
    { key: 'snapshotAt', label: 'Время снимка', render: row => formatSourceTime(row.snapshotAt) },
    { key: 'previousDate', label: 'Сопоставимая дата' }, { key: 'previousOrders', label: 'Ранее', render: row => formatNumber(row.previousOrders) },
    { key: 'note', label: 'Комментарий' },
  ]} rows={period?.deliveries || []} empty="Снимков предзаказов для периода нет." />;
}

export function Dynamics({ dynamics }) {
  if (!dynamics) return <p className="flordvor-empty">Динамика поставок в снимке отсутствует.</p>;
  return <><p className="flordvor-note">Группа: {dynamics.folderName || '—'}. Срез: {formatSourceTime(dynamics.capturedAt)}.</p>
    <DataTable columns={[
      { key: 'start', label: 'Начало' }, { key: 'end', label: 'Конец' }, { key: 'type', label: 'Цикл', render: row => row.type === 'monday' ? 'Понедельник' : row.type === 'thursday' ? 'Четверг' : '—' },
      { key: 'opening', label: 'Остаток на входе', render: row => formatNumber(row.opening) },
      { key: 'receipts', label: 'Количество приёмок', render: row => formatNumber(row.receipts?.quantity) },
      { key: 'orders', label: 'Количество в заказах', render: row => formatNumber(row.orders?.quantity) },
      { key: 'previousOrders', label: 'Количество год назад', render: row => formatNumber(row.previousOrders?.quantity) },
      { key: 'partial', label: 'Полнота', render: row => row.partial ? 'Неполный период' : 'Полный период' },
    ]} rows={dynamics.cycles || []} /></>;
}

function Methodology() {
  return <div className="flordvor-method"><h3>Правила расчёта</h3><ul>
    <li>В показателях участвуют только проведённые документы (`applicable=true`).</li>
    <li>Выручка показывается после возвратов; валовая выручка и возвраты доступны отдельно.</li>
    <li>Количество берётся из строк продаж и уменьшается строками возвратов; услуги исключаются.</li>
    <li>Приёмки считаются по проведённым поставкам склада «Опт», с полными товарными строками.</li>
    <li>Предзаказ подтверждается историческим снимком. Отсутствующий снимок не равен нулю.</li>
    <li>Догрузка заменяет прежнее состояние изменённого документа по ID и учитывает удаление, снятие проведения, перенос даты, склада или группы.</li>
    <li>Ошибка источника не затирает последний проверенный снимок.</li>
  </ul></div>;
}

function ImportPanel({ onImported }) {
  const [kind, setKind] = useState('verified_snapshot');
  const [label, setLabel] = useState('Последний проверенный снимок');
  const [sourceFactAt, setSourceFactAt] = useState('');
  const [payload, setPayload] = useState(null);
  const [message, setMessage] = useState('');
  const readFile = async event => {
    const file = event.target.files?.[0]; if (!file) return;
    try { setPayload(JSON.parse(await file.text())); setMessage(`Подготовлен файл: ${file.name}`); }
    catch { setPayload(null); setMessage('Файл не является корректным JSON.'); }
  };
  const submit = async () => {
    if (!payload) return setMessage('Сначала выберите JSON-файл.');
    try {
      const response = await api.post('/tenant/flordvor/import', { kind, label, source_fact_at: sourceFactAt ? new Date(sourceFactAt).toISOString() : null, payload });
      setMessage(response.data.created ? 'Данные импортированы.' : 'Такие данные уже были импортированы.'); onImported();
    } catch (error) { setMessage(error.response?.data?.detail || 'Импорт не выполнен.'); }
  };
  return <details className="flordvor-import"><summary>Импорт данных панели</summary>
    <p>Импорт сохраняет исходное время фактов и не считается свежей синхронизацией.</p>
    <div className="flordvor-import-grid"><select value={kind} onChange={event => setKind(event.target.value)}>{IMPORT_KINDS.map(([value, text]) => <option key={value} value={value}>{text}</option>)}</select>
      <input value={label} onChange={event => setLabel(event.target.value)} placeholder="Название импорта" />
      <label>Время фактов <input type="datetime-local" value={sourceFactAt} onChange={event => setSourceFactAt(event.target.value)} /></label>
      <input type="file" accept="application/json,.json" onChange={readFile} /><button type="button" onClick={submit}>Импортировать</button></div>
    {message && <p>{message}</p>}</details>;
}

function SyncPanel({ status, onChanged }) {
  const [busy, setBusy] = useState(''), [message, setMessage] = useState('');
  const [job,setJob]=useState(status.current_job||null);
  const jobId=job?.id,jobStatus=job?.status;
  useEffect(()=>{if(status.current_job)setJob(status.current_job);},[status.current_job]);
  useEffect(()=>{
    if(!jobId||!['queued','running'].includes(jobStatus))return undefined;
    let active=true;
    const timer=setInterval(async()=>{
      try{
        const {data}=await api.get(`/tenant/flordvor/sync/jobs/${jobId}`);
        if(!active)return;
        setJob(data);
        if(data.status==='success'){
          setMessage(data.mode==='preflight'?'Соединение и аудит товарных групп проверены.':'Данные проверены, новый снимок опубликован.');
          await onChanged();
        }else if(data.status==='failed'){
          setMessage(data.error||'Обновление не завершено. Прежний проверенный снимок сохранён.');
          await onChanged();
        }
      }catch(error){if(active)setMessage(error.response?.data?.detail||'Не удалось проверить состояние обновления.');}
    },2000);
    return()=>{active=false;clearInterval(timer);};
  },[jobId,jobStatus,onChanged]);
  const collecting=job&&['queued','running'].includes(job.status);
  const run = async (action, path) => {
    setBusy(action); setMessage('');
    try {
      const {data}=await api.post(path);
      if(!data?.job)throw new Error('Сервер не подтвердил постановку обновления в очередь.');
      setJob(data.job);
      setMessage('Задание принято. Обновление продолжится на сервере, даже если закрыть страницу.');
    } catch (error) {
      setMessage(error.response?.data?.detail || error.message || 'Операция не выполнена.');
      await onChanged();
    } finally { setBusy(''); }
  };
  return <section className="flordvor-sync"><h3>Источник МоегоСклада</h3>
    {collecting&&<p role="status">{job.status==='queued'?'Обновление ожидает запуска.':'Идёт получение и проверка данных.'} Последний проверенный снимок остаётся доступен.</p>}
    <div className="flordvor-status-grid">
      <span><strong>Токен:</strong> {status.token_configured ? 'настроен на сервере' : 'не настроен'}</span>
      <span><strong>Соединение:</strong> {status.source_status || '—'}</span>
      <span><strong>Последняя проверка:</strong> {formatSourceTime(status.connection_checked_at)}</span>
      <span><strong>Последнее успешное обновление:</strong> {formatSourceTime(status.last_successful_update_at)}</span>
      <span><strong>Граница данных:</strong> {formatSourceTime(status.source_boundary)}</span>
      <span><strong>База догрузки:</strong> {status.incremental_ready ? 'готова' : 'отсутствует — нужен полный пересбор'}</span>
    </div>
    {status.last_error && <div className="flordvor-error"><strong>Последняя ошибка:</strong> {status.last_error}</div>}
    {job?.status==='failed' && <div role="alert" className="flordvor-error">{job.error || 'Обновление не завершено; прежний проверенный снимок сохранён.'}</div>}
    <div className="flordvor-sync-actions">
      <button type="button" disabled={!status.token_configured || !!busy || collecting} onClick={() => run('preflight', '/tenant/flordvor/sync/preflight')}>Проверить соединение</button>
      <button type="button" disabled={!status.source_connected || !status.incremental_ready || !!busy || collecting} onClick={() => run('incremental', '/tenant/flordvor/sync')}>Догрузить изменения</button>
      <button type="button" disabled={!status.source_connected || !!busy || collecting} onClick={() => run('full', '/tenant/flordvor/sync/full')}>Полный пересбор</button>
    </div>
    {busy && <p>Операция выполняется. Не запускайте второй сбор.</p>}
    {message && <p>{message}</p>}
  </section>;
}

function FlordvorDashboard() {
  const [status, setStatus] = useState(null), [snapshot, setSnapshot] = useState(null), [error, setError] = useState('');
  const [tab, setTab] = useState('summary'), [periodKey, setPeriodKey] = useState('');
  const load = async () => {
    setError('');
    try {
      const response = await api.get('/tenant/flordvor/status'); setStatus(response.data); setSnapshot(response.data.current_snapshot);
      const items = response.data.current_snapshot?.payload?.periods || [];
      if (items.length) setPeriodKey(previous => items.some(item => item.key === previous) ? previous : items[0].key);
    } catch (requestError) { setError(requestError.response?.data?.detail || 'Не удалось открыть раздел «Флордвор».'); }
  };
  useEffect(() => { load(); }, []);
  const data = snapshot?.payload;
  const periods = useMemo(() => [...(data?.periods || []).map(item => ({ ...item, selectorLabel: `Месяц · ${item.label}` })), ...(data?.weeks || []).map(item => ({ ...item, selectorLabel: `Неделя · ${item.label}` }))], [data]);
  const period = periods.find(item => item.key === periodKey) || periods[0];
  return <section className="flordvor"><header className="flordvor-header"><div><h2>Флордвор</h2><p>Аналитика продаж, приёмок, предзаказов и поставок</p></div><button type="button" onClick={load}>Обновить экран</button></header>
    {error && <div className="flordvor-error">{error}</div>}
    {status && !status.token_configured && <div className="flordvor-disconnected"><strong>Источник не подключён.</strong> Сохранённые данные доступны; установка токена не меняет время снимка и не запускает обновление.</div>}
    {status && <SyncPanel status={status} onChanged={load} />}
    {snapshot ? <div className="flordvor-times"><span><strong>Подтверждённая граница данных:</strong> {formatSourceTime(snapshot.source_fact_at)}</span><span><strong>Расчёт завершён:</strong> {formatSourceTime(snapshot.source_recorded_at)}</span><span><strong>Опубликовано в Дэнаурум:</strong> {formatSourceTime(snapshot.imported_at)}</span>{snapshot.origin === 'import' && <span className="flordvor-imported">Импортированный снимок</span>}</div> : <div className="flordvor-empty">Проверенный снимок ещё не импортирован.</div>}
    <ImportPanel onImported={load} />
    {data && <><div className="flordvor-controls"><select aria-label="Период" value={period?.key || ''} onChange={event => setPeriodKey(event.target.value)}>{periods.map(item => <option key={`${item.kind}-${item.key}`} value={item.key}>{item.selectorLabel}</option>)}</select><span>Склад: {data.warehouseName || '—'}</span></div>
      <nav className="flordvor-tabs">{TABS.map(([value, text]) => <button type="button" key={value} className={tab === value ? 'active' : ''} onClick={() => setTab(value)}>{text}</button>)}</nav>
      <div className="flordvor-content">{tab === 'summary' && <Summary period={period} />}{tab === 'wholesale' && <DataTable columns={financialColumns} rows={period?.groups || []} />}{tab === 'retail' && <Retail retail={period?.retail} />}{tab === 'operations' && <Operations period={period} />}{tab === 'preorders' && <Preorders period={period} />}{tab === 'dynamics' && <Dynamics dynamics={data.deliveryDynamics} />}{tab === 'method' && <Methodology />}</div></>}
  </section>;
}
export default FlordvorDashboard;
